import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { Edit as EditIcon, Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { useState } from "react";
import { MutationsSection } from "../MutationsSection";
import {
  useCoolingRegimeTemperatureOptions,
  useCoolingRegimePriorityOptions,
} from "../../hooks/useCoolingRegimeProductGroup";
import { formatDate, formatDateTime } from "../../utils/dateFormat";

interface CoolingRegimeData {
  // Product/Genus code info
  code?: string | number | null;
  applicationCode?: string | number | null;
  relativeHumidityIndication?: string | null;

  // Temperature codes
  coolingTemperatureCodeShortTerm?: number | null;
  coolingTemperatureCodeLongTerm?: number | null;
  coolingTemperatureCodeShortTermDisplacement?: number | null;
  coolingTemperatureCodeLongTermDisplacement?: number | null;

  // Temperature descriptions (for display)
  temperatureShortTerm?: { description?: string | null } | null;
  temperatureLongTerm?: { description?: string | null } | null;
  temperatureShortTermDisplacement?: { description?: string | null } | null;
  temperatureLongTermDisplacement?: { description?: string | null } | null;

  // Priority codes
  coolingPriorityCodeShortTerm?: number | null;
  coolingPriorityCodeLongTerm?: number | null;

  // Priority descriptions (for display)
  priorityShortTerm?: { description?: string | null } | null;
  priorityLongTerm?: { description?: string | null } | null;

  // Metadata
  created?: Date | null;
  createdBy?: string | null;
  lastModified?: Date | null;
  lastModifiedBy?: string | null;
}

export interface EditableCoolingRegimeDetailsProps {
  data: CoolingRegimeData | null | undefined;
  isLoading: boolean;
  error?: Error | null;
  onSaveTemperature: (
    field: keyof TemperatureUpdates,
    value: number | null,
  ) => Promise<void>;
  onSavePriority: (
    field: keyof PriorityUpdates,
    value: number | null,
  ) => Promise<void>;
  isSaving?: boolean;
  codeLabel?: string;
  showPriorities?: boolean;
  /** Optional handler to edit relative humidity indication (J/N). When not provided, field remains read-only. */
  onSaveRelativeHumidity?: (value: "J" | "N" | null) => Promise<void>;
  /** Optional extra row rendered under the product code block (e.g., genus info). */
  extraProductRow?: { label: string; value: React.ReactNode };
  /** Optional delete handler; when provided, a trash icon appears in edit mode. */
  onDelete?: () => Promise<void>;
  /** Whether a delete operation is in progress. */
  isDeleting?: boolean;
  /** Whether editing existing regime is allowed (requires product regime). Defaults to true. */
  canEdit?: boolean;
  /** Optional callback to start create flow when no product regime exists. */
  onCreateRequested?: () => void;
}

export interface TemperatureUpdates {
  shortTermMain: number | null;
  longTermMain: number | null;
  shortTermDisplacementMain: number | null;
  longTermDisplacementMain: number | null;
}

export interface PriorityUpdates {
  shortTerm: number | null;
  longTerm: number | null;
}

interface TemperatureRow {
  regime: string;
  temperature: string;
  fieldKey: string;
}

interface PriorityRow {
  regime: string;
  guideline: string;
  fieldKey: string;
}

/**
 * Maps data to temperature rows for display
 */
function mapTemperatureRows(data: CoolingRegimeData): TemperatureRow[] {
  const rows: TemperatureRow[] = [];

  if (data.temperatureShortTerm) {
    rows.push({
      regime: "Regime < 24u",
      temperature: data.temperatureShortTerm.description ?? "-",
      fieldKey: "shortTermMain",
    });
  }

  if (data.temperatureLongTerm) {
    rows.push({
      regime: "Regime > 24u",
      temperature: data.temperatureLongTerm.description ?? "-",
      fieldKey: "longTermMain",
    });
  }

  if (data.temperatureShortTermDisplacement) {
    rows.push({
      regime: "Uitwijk < 24u",
      temperature: data.temperatureShortTermDisplacement.description ?? "-",
      fieldKey: "shortTermDisplacementMain",
    });
  }

  if (data.temperatureLongTermDisplacement) {
    rows.push({
      regime: "Uitwijk > 24u",
      temperature: data.temperatureLongTermDisplacement.description ?? "-",
      fieldKey: "longTermDisplacementMain",
    });
  }

  return rows;
}

/**
 * Maps data to priority rows for display
 */
function mapPriorityRows(data: CoolingRegimeData): PriorityRow[] {
  const rows: PriorityRow[] = [];

  if (data.priorityShortTerm) {
    rows.push({
      regime: "Regime < 24u",
      guideline: data.priorityShortTerm.description ?? "-",
      fieldKey: "shortTerm",
    });
  }

  if (data.priorityLongTerm) {
    rows.push({
      regime: "Regime > 24u",
      guideline: data.priorityLongTerm.description ?? "-",
      fieldKey: "longTerm",
    });
  }

  return rows;
}

export function EditableCoolingRegimeDetails({
  data,
  isLoading,
  error,
  onSaveTemperature,
  onSavePriority,
  isSaving = false,
  codeLabel = "Productgroep code",
  showPriorities = true,
  onSaveRelativeHumidity,
  extraProductRow,
  onDelete,
  isDeleting = false,
  canEdit = true,
  onCreateRequested,
}: EditableCoolingRegimeDetailsProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const { data: temperatureOptions } = useCoolingRegimeTemperatureOptions();
  const { data: priorityOptions } = useCoolingRegimePriorityOptions();

  // Track which field is currently saving
  const [savingField, setSavingField] = useState<string | null>(null);

  const handleToggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleTemperatureChange = async (
    field: keyof TemperatureUpdates,
    value: number | null,
  ) => {
    setSavingField(`temperature_${field}`);
    try {
      await onSaveTemperature(field, value);
    } finally {
      setSavingField(null);
    }
  };

  const handlePriorityChange = async (
    field: keyof PriorityUpdates,
    value: number | null,
  ) => {
    setSavingField(`priority_${field}`);
    try {
      await onSavePriority(field, value);
    } finally {
      setSavingField(null);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">
          Fout bij laden van koelregime details: {error.message}
        </Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Typography>Geen gegevens gevonden</Typography>
      </Box>
    );
  }

  const temperatureRows = mapTemperatureRows(data);
  const priorityRows = mapPriorityRows(data);

  // Mapping from field keys to actual field names for temperature
  const temperatureFieldMap: Record<string, keyof TemperatureUpdates> = {
    shortTermMain: "shortTermMain",
    longTermMain: "longTermMain",
    shortTermDisplacementMain: "shortTermDisplacementMain",
    longTermDisplacementMain: "longTermDisplacementMain",
  };

  // Mapping from row fieldKey to data field names for temperature codes
  const temperatureCodeFieldMap: Record<string, keyof CoolingRegimeData> = {
    shortTermMain: "coolingTemperatureCodeShortTerm",
    longTermMain: "coolingTemperatureCodeLongTerm",
    shortTermDisplacementMain: "coolingTemperatureCodeShortTermDisplacement",
    longTermDisplacementMain: "coolingTemperatureCodeLongTermDisplacement",
  };

  // Mapping from field keys to actual field names for priority
  const priorityFieldMap: Record<string, keyof PriorityUpdates> = {
    shortTerm: "shortTerm",
    longTerm: "longTerm",
  };

  // Normalize legacy RH values: treat 'Y' as 'J' for UI binding
  const normalizedRhValue = (() => {
    const raw = (data?.relativeHumidityIndication ?? "")
      .toString()
      .toUpperCase();
    if (raw === "J" || raw === "Y") return "J";
    if (raw === "N") return "N";
    return "";
  })();

  return (
    <Box>
      {/* Header actions: Edit (pencil) on the left, Delete (trash) on the right */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mb: 2 }}>
        {canEdit ? (
          !isEditMode ? (
            <IconButton
              aria-label="Bewerken"
              color="primary"
              onClick={handleToggleEditMode}
            >
              <EditIcon />
            </IconButton>
          ) : (
            <IconButton
              aria-label="Stop bewerken"
              color="secondary"
              onClick={handleCancelEdit}
            >
              <CloseIcon />
            </IconButton>
          )
        ) : (
          onCreateRequested && (
            <IconButton
              aria-label="Nieuw koelregime"
              color="primary"
              onClick={onCreateRequested}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                "&:hover": { bgcolor: "primary.dark" },
              }}
            >
              <AddIcon />
            </IconButton>
          )
        )}
        {onDelete && (
          <IconButton
            aria-label="Verwijder koelregime"
            color="error"
            onClick={onDelete}
            disabled={isDeleting}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      {/* Three panels in a responsive grid */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Panel 1: Product code and Relatieve Vochtigheid indicatie */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid #eee",
              height: "100%",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Productcode en Relatieve Vochtigheid indicatie
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <strong>{codeLabel}</strong>
                    </TableCell>
                    <TableCell>{data.code ?? "-"}</TableCell>
                  </TableRow>
                  {extraProductRow && (
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <strong>{extraProductRow.label}</strong>
                      </TableCell>
                      <TableCell>{extraProductRow.value}</TableCell>
                    </TableRow>
                  )}
                  {data.applicationCode !== undefined && data.applicationCode !== null && (
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <strong>Toepassing</strong>
                      </TableCell>
                      <TableCell>{data.applicationCode ?? "-"}</TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <strong>Relatieve Vochtigheid indicatie</strong>
                    </TableCell>
                    <TableCell>
                      {isEditMode && onSaveRelativeHumidity ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={normalizedRhValue as string}
                            displayEmpty
                            onChange={async (e) => {
                              setSavingField("relativeHumidity");
                              try {
                                const v = (e.target.value as string) || "";
                                await onSaveRelativeHumidity(
                                  v === "J" ? "J" : v === "N" ? "N" : null,
                                );
                              } finally {
                                setSavingField(null);
                              }
                            }}
                            disabled={
                              isSaving || savingField === "relativeHumidity"
                            }
                            renderValue={(val) => {
                              const v = String(val).toUpperCase();
                              if (!v) return "—";
                              return v === "J" ? "Ja" : "Nee";
                            }}
                          >
                            <MenuItem value="J">Ja</MenuItem>
                            <MenuItem value="N">Nee</MenuItem>
                          </Select>
                        </FormControl>
                      ) : ["J", "j", "Y", "y"].includes(
                          String(data.relativeHumidityIndication),
                        ) ? (
                        "Ja"
                      ) : (
                        "Nee"
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Panel 2: Cooling temperatures */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid #eee",
              height: "100%",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Koeltemperaturen
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Regime</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Temperatuur</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {temperatureRows.length > 0 ? (
                    temperatureRows.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.regime}</TableCell>
                        <TableCell>
                          {isEditMode ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={
                                  data[
                                    temperatureCodeFieldMap[
                                      row.fieldKey
                                    ] as keyof CoolingRegimeData
                                  ] ?? ""
                                }
                                onChange={(e) => {
                                  const fieldKey =
                                    temperatureFieldMap[row.fieldKey];
                                  if (fieldKey) {
                                    handleTemperatureChange(
                                      fieldKey,
                                      e.target.value
                                        ? Number(e.target.value)
                                        : null,
                                    );
                                  }
                                }}
                                displayEmpty
                                disabled={
                                  savingField === `temperature_${row.fieldKey}`
                                }
                              >
                                <MenuItem value="">
                                  <em>Selecteer temperatuur</em>
                                </MenuItem>
                                {temperatureOptions
                                  ?.filter(
                                    (temp) =>
                                      temp.coolingRegimeTemperatureId != null,
                                  )
                                  .map((temp) => (
                                    <MenuItem
                                      key={temp.coolingRegimeTemperatureId!}
                                      value={temp.coolingRegimeTemperatureId!}
                                    >
                                      {temp.description}
                                      {savingField ===
                                        `temperature_${row.fieldKey}` &&
                                        " (Opslaan...)"}
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          ) : (
                            row.temperature
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Geen temperaturen beschikbaar
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Panel 3: Cooling priorities */}
        {showPriorities && (
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #eee",
                height: "100%",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Koelprioriteiten
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Prioriteit</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Richtlijn</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {priorityRows.length > 0 ? (
                      priorityRows.map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.regime}</TableCell>
                          <TableCell>
                            {isEditMode ? (
                              <FormControl fullWidth size="small">
                                <Select
                                  value={
                                    data[
                                      `coolingPriorityCode${
                                        row.fieldKey.charAt(0).toUpperCase() +
                                        row.fieldKey.slice(1)
                                      }` as keyof CoolingRegimeData
                                    ] ?? ""
                                  }
                                  onChange={(e) => {
                                    const fieldKey =
                                      priorityFieldMap[row.fieldKey];
                                    if (fieldKey) {
                                      handlePriorityChange(
                                        fieldKey,
                                        e.target.value
                                          ? Number(e.target.value)
                                          : null,
                                      );
                                    }
                                  }}
                                  displayEmpty
                                  disabled={
                                    savingField === `priority_${row.fieldKey}`
                                  }
                                >
                                  <MenuItem value="">
                                    <em>Selecteer prioriteit</em>
                                  </MenuItem>
                                  {priorityOptions?.map((option) => (
                                    <MenuItem
                                      key={option.coolingRegimePriorityId ?? 0}
                                      value={
                                        option.coolingRegimePriorityId ?? ""
                                      }
                                    >
                                      {option.description}
                                      {savingField ===
                                        `priority_${row.fieldKey}` &&
                                        " (Opslaan...)"}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              row.guideline
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          Geen prioriteiten beschikbaar
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>

      <MutationsSection
        createdBy={data.createdBy}
        createdDate={data.created}
        lastModifiedBy={data.lastModifiedBy}
        lastModifiedDate={data.lastModified}
      />
    </Box>
  );
}
